import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable2 = ({ data }) => {
  const pdfRef = useRef();

  const genderGapData = data.map((item) => item.male - item.female);

  // Chart Config
  const chartData = {
    labels: data.map((item) => item.year),
    datasets: [
      {
        label: "Gender Gap (Male - Female)",
        data: genderGapData,
        borderColor: "#FE9A37",
        backgroundColor: "#FE9A3780",
        borderWidth: 3,
        tension: 0.4,
        fill: true,
        pointBackgroundColor: "#FE9A37",
        pointBorderColor: "#FE9A37",
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 9,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📈 Gender Employment Gap Trend (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.raw.toLocaleString("en-US")} Employees`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: "Gap (Male - Female)" },
      },
      x: {
        title: { display: true, text: "Year" },
      },
    },
  };

  // Summary Insights
  const startGap = genderGapData[0];
  const finalGap = genderGapData[genderGapData.length - 1];
  const growthPercentage = (((finalGap - startGap) / startGap) * 100).toFixed(
    1,
  );

  const peakGap = data.reduce(
    (max, d) =>
      d.male - d.female > max.gap
        ? { year: d.year, gap: d.male - d.female }
        : max,
    { year: data[0].year, gap: data[0].male - data[0].female },
  );

  // PDF Download
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        const logoWidth = 20; // ideal header width
        const logoHeight = 20; // adjust ratio if needed

        pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY,
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right",
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      // ===== FINAL FOOTER SAFE AREA =====

      const footerTopY = pageHeight - 40; // start footer 40mm from bottom
      const footerContentY = pageHeight - 28; // logos row
      const footerBottomTextY = pageHeight - 10; // bottom text row

      // Top Divider Line
      pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

      // UtahData logo
      const utahWidth = 18;
      const utahHeight = 18;
      pdf.addImage(
        utahData,
        "PNG",
        20,
        footerContentY - 8,
        utahWidth,
        utahHeight,
      );

      // Built by
      pdf.setFontSize(10);
      pdf.text("Built by", 45, footerContentY);

      // BODEX logo
      const bodexWidth = 36;
      const bodexHeight = bodexWidth * 0.3;
      pdf.addImage(
        bodex,
        "PNG",
        60,
        footerContentY - 6,
        bodexWidth,
        bodexHeight,
      );

      // Using text
      pdf.text("using", 102, footerContentY);

      // KPI logo
      pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

      // Version
      pdf.text(APP_VERSION, 140, footerContentY);

      // Bottom Info Line
      pdf.setFontSize(8);
      pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        pageWidth - 10,
        footerBottomTextY,
        null,
        null,
        "right",
      );

      pdf.save("gender_gap_trend.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Gender Employment Gap (Male - Female) Trend
        </h2>

        {/* Summary Insights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Gap Growth (2018–2024)
            </p>
            <p className="text-xl font-bold text-blue-800">
              +{growthPercentage}%
            </p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Peak Gap Year</p>
            <p className="text-xl font-bold text-yellow-800">{peakGap.year}</p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">2024 Gap</p>
            <p className="text-xl font-bold text-green-800">
              {Number(finalGap).toLocaleString("en-US")}
            </p>
          </div>
        </div>

        {/* Table + Chart Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Year</th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Male
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Female
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Gap
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr
                      key={row.year}
                      className={`${
                        row.year === 2024
                          ? "bg-green-100 font-bold"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <td className="p-2 text-left">{row.year}</td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {Number(row.male).toLocaleString("en-US")}
                      </td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {Number(row.female).toLocaleString("en-US")}
                      </td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {Number(row.male - row.female).toLocaleString("en-US")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable2;

